import { useEffect, useRef, useState } from "react";

// Hooks
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "app/hooks";

// Store
import { LD12Chart } from "store/slice/charts";
import { getIsFetchingReportStatus, getReportStatus, IPaceAssessmentReportStatusAssessment } from "store/slice/Pace";

// Mui
import theme from "assets/styles/themes";

// Enums
import { urlPaths } from "enums/urlPaths";

// Services
import { getAssessmentChart } from "services/pace/dashboard.service";

// Utils
import { isEmpty } from "lodash";
import { format } from "date-fns";
import { mergeLD12Series, mergeSeriesIntoLD12Series, getRetakeAssessmentDate } from "utils";
import { convertChartLayout } from "pages/BehavioralComparison/adapters/converter.chart.layout";
import { convertIndividualsData } from "pages/BehavioralComparison/adapters/converter.chart.individuals";

// Types
import { RequestInfo } from "store/slice/store.types";
import { TBCSectionData } from "pages/BehavioralComparison/adapters/converter.chart";
import { COLORS } from "constants/chartCommons";
import usePaceLandingLayout from "components/Pace/PaceLandingLayout/usePaceLandingLayout";

export interface IPreviousReports {
    id: number;
    completedDate: Date | string;
    checked: boolean;
    label: string;
    subLabel: string;
}

interface IChartData {
    status: RequestInfo;
    data: LD12Chart;
}

const useMyPace = () => {
    const navigate = useNavigate();

    const reportsData = useAppSelector(getReportStatus);
    const reportRequest = useAppSelector(getIsFetchingReportStatus);

    const hasCompletedAssessment = Boolean(reportsData?.count_completed !== 0);
    const { handleStartPace } = usePaceLandingLayout();

    const [chartData, setChartData] = useState<IChartData>({ status: "pristine", data: {} as LD12Chart });
    const [previousReportsList, setPreviousReportsList] = useState<IPreviousReports[]>([]);

    const [status, setStatus] = useState({
        barColor: theme.palette.error.main,
        legend: "Overdue!",
        isRetakeAvailable: true,
    });

    const retakeAvailableOn = useRef("");
    const shouldShowBanner = reportsData?.count_completed === 1 && status.isRetakeAvailable === true;

    const handleRetestPace = async () => {
        handleStartPace();
    };

    const handlenavigateToMyDashboard = () => {
        navigate(urlPaths.PaceLabsDashboard);
    };

    const handleUpdatePreviousList = (ev: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const isChecked = ev.target.checked;

        setPreviousReportsList((prevData) =>
            prevData.map((report) => {
                const checked = report.id === id ? ev.target.checked : report.checked;
                return {
                    ...report,
                    checked,
                };
            })
        );

        const isDataInChart = chartData.data.series_labels.find((serieLabel) => serieLabel.entity_id === id);

        if (!isDataInChart) {
            handlFetchChartData(id);
        } else {
            setChartData((prevData) => {
                return {
                    ...prevData,
                    data: {
                        ...prevData.data,
                        series_labels: prevData.data.series_labels.map((serieLabel) => {
                            return {
                                ...serieLabel,
                                enabled: serieLabel.entity_id === id ? isChecked : serieLabel.enabled,
                            };
                        }),
                    },
                };
            });
        }
    };

    const handleSetPreviousList = (assessments: IPaceAssessmentReportStatusAssessment[]) => {
        const finished = assessments.filter((item) => item.meta.status === "FINISHED_COMPLETED");
        const assessmentCount = finished.length;

        const previousReportsList = assessments
            .filter((item) => item.meta.status === "FINISHED_COMPLETED")
            .map((item, i) => {
                const isMostRecent = i === 0;
                const isFirst = i === assessmentCount - 1;
                const formattedDate = format(new Date(item.meta.completed), "MMM yyyy");

                const label = isMostRecent ? `Current: ${formattedDate}` : formattedDate;
                let subLabel = "";

                if (assessmentCount === 1 || isFirst) {
                    subLabel = "First PACE Test";
                } else {
                    subLabel = `Retake #${assessmentCount - 1 - i}`;
                }

                return {
                    id: item.user_assessment_id,
                    label,
                    subLabel,
                    completedDate: item.meta.completed,
                    checked: isMostRecent,
                };
            });

        setPreviousReportsList(previousReportsList);
    };

    const handleSetStatus = (lastCompletedOn: string) => {
        const {
            retakeAvailableOn: retake,
            statusBarColor,
            statusLegend,
            isRetakeAvailable,
        } = getRetakeAssessmentDate(lastCompletedOn);

        retakeAvailableOn.current = retake;

        setStatus({
            barColor: statusBarColor,
            legend: statusLegend,
            isRetakeAvailable: isRetakeAvailable,
        });
    };

    const handlFetchChartData = async (assementId: number) => {
        try {
            setChartData((prevData) => ({
                ...prevData,
                status: prevData.status === "done" ? prevData.status : "fetching",
            }));

            const result = await getAssessmentChart([assementId], true);
            const data = result.data.graphs;

            const axes = convertChartLayout(data[0]);
            const individualsData = data.flatMap((d) => convertIndividualsData(d, "date")) as TBCSectionData[];

            const series = individualsData.map((el) => el?.series);
            const newSeriesLabels = individualsData
                .filter((el) => el !== undefined)
                .map((el) => {
                    if (el?.series) {
                        const { series, ...others } = el;
                        return {
                            ...others,
                        };
                    }
                    return el;
                });

            const newSerie = mergeSeriesIntoLD12Series(series);

            setChartData((prevData) => {
                const series_labels = [...(prevData.data.series_labels || []), ...newSeriesLabels].map((label, i) => ({
                    ...label,
                    color: COLORS[i],
                }));

                const series = mergeLD12Series(prevData.data.series, newSerie);

                return {
                    status: "done",
                    data: {
                        name: "Behavioral Comparison",
                        axes,
                        series_labels,
                        series,
                        data_errors: [],
                        leadership_insight: { insight: "", explainer: [], text: "", questions: [] },
                    },
                };
            });
        } catch (error) {
            console.error(error);
            setChartData((data) => ({ ...data, status: "error" }));
        }
    };

    // Init
    useEffect(() => {
        if (!reportsData?.user_assessments.length) return;

        if (isEmpty(chartData.data) && chartData.status === "pristine") {
            const assessment = reportsData.user_assessments.find((item) => item.meta.status === "FINISHED_COMPLETED");

            if (assessment?.user_assessment_id) {
                handlFetchChartData(assessment.user_assessment_id);
                handleSetStatus(assessment.meta.completed);
            }

            handleSetPreviousList(reportsData.user_assessments);
        }
    }, [chartData, reportsData]);

    return {
        status,
        chartData,
        reportRequest,
        shouldShowBanner,
        retakeAvailableOn,
        previousReportsList,
        hasCompletedAssessment,
        handleRetestPace,
        handlenavigateToMyDashboard,
        handleUpdatePreviousList,
    };
};

export default useMyPace;
